import { computed, getCurrentInstance, inject, defineComponent, h, ref, isVue3, provide } from 'vue-demi';
import { CachedSyncIterable } from 'cached-iterable';
import { mapBundleSync } from '@fluent/sequence';
import { negotiateLanguages } from '@fluent/langneg';
import { FluentBundle } from '@fluent/bundle';

function warn(message) {
    if (process.env.NODE_ENV !== 'production') {
        console.warn(`[fluent-vue] ${message}`);
    }
}

function getOrderedBundles(requestedLocale, bundles) {
    if (!Array.isArray(requestedLocale)) {
        requestedLocale = [requestedLocale];
    }
    const avaliableLocales = bundles.flatMap((bundle) => bundle.locales);
    const negotiatedLocales = negotiateLanguages(requestedLocale, avaliableLocales, {
        strategy: 'filtering',
    });
    const newBundles = negotiatedLocales.map((locale) => bundles.find((bundle) => bundle.locales.includes(locale)));
    const dedupeBundles = newBundles
        .filter((bundle, i) => newBundles.indexOf(bundle) === i)
        .filter((bundle) => bundle != null);
    return dedupeBundles;
}

class TranslationContext {
    constructor(locale, bundles) {
        this.format = (key, value) => {
            var _a;
            const context = this.getBundle(key);
            const message = this.getMessage(context, key);
            return (_a = this._format(context, message, value)) !== null && _a !== void 0 ? _a : key;
        };
        this.formatAttrs = (key, value) => {
            var _a;
            const context = this.getBundle(key);
            const message = this.getMessage(context, key);
            return (_a = this._formatAttrs(context, message, value)) !== null && _a !== void 0 ? _a : {};
        };
        this.formatWithAttrs = (key, value) => {
            var _a;
            const context = this.getBundle(key);
            const message = this.getMessage(context, key);
            const formatValue = this._format(context, message, value);
            return {
                value: formatValue !== null && formatValue !== void 0 ? formatValue : key,
                attributes: (_a = this._formatAttrs(context, message, value)) !== null && _a !== void 0 ? _a : {},
                hasValue: formatValue !== null,
            };
        };
        this.$t = this.format;
        this.$ta = this.formatAttrs;
        this.locale = locale;
        this.bundles = bundles;
        this.bundlesIterable = computed(() => CachedSyncIterable.from(getOrderedBundles(this.locale.value, bundles.value)));
    }
    getBundle(key) {
        return mapBundleSync(this.bundlesIterable.value, key);
    }
    getMessage(bundle, key) {
        const message = bundle === null || bundle === void 0 ? void 0 : bundle.getMessage(key);
        if (message === undefined) {
            warn(`Could not find translation for key [${key}]`);
            return null;
        }
        return message;
    }
    formatPattern(bundle, message, value) {
        const errors = [];
        const formatted = bundle.formatPattern(message, value, errors);
        for (const error of errors) {
            warn(`Error when formatting: ${error}`);
        }
        return formatted;
    }
    _format(context, message, value) {
        if (context === null || message === null || message.value === null) {
            return null;
        }
        return this.formatPattern(context, message.value, value);
    }
    _formatAttrs(context, message, value) {
        if (context === null || message === null) {
            return null;
        }
        const result = {};
        for (const [attrName, attrValue] of Object.entries(message.attributes)) {
            result[attrName] = this.formatPattern(context, attrValue, value);
        }
        return result;
    }
}

/**
 * Copies options, messages and terms from parent bundle.
 *
 * Allows splitting localization into multiple bundles and share
 * common messages and terms betwean bundles.
 */
function inheritBundle(locale, parent) {
    // Copy options
    const bundle = new FluentBundle(locale, {
        functions: parent._functions,
        useIsolating: parent._useIsolating,
        transform: parent._transform,
    });
    // Copy messages and terms
    bundle._terms = new Map(parent._terms);
    bundle._messages = new Map(parent._messages);
    return bundle;
}

const RootContextSymbol = Symbol('root-context');

function getContext(rootContext, instance) {
    var _a;
    if (instance == null) {
        return rootContext;
    }
    const target = (_a = instance.$options) !== null && _a !== void 0 ? _a : instance.type;
    if (target._fluent != null) {
        return target._fluent;
    }
    let context = rootContext;
    // If we override messages in a component
    // create new translation context with new bundles
    if (target.fluent) {
        const overriddenBundles = computed(() => {
            const allLocales = rootContext.bundles.value.flatMap((bundle) => bundle.locales);
            return Object.entries(target.fluent)
                .map(([locale, resources]) => {
                const locales = locale.split(/[\s+,]/);
                const parentLocale = negotiateLanguages(locales, allLocales, {
                    strategy: 'lookup',
                    defaultLocale: locales[0],
                })[0];
                const parentBundle = rootContext.bundles.value.find((bundle) => bundle.locales.includes(parentLocale));
                if (!parentBundle) {
                    warn(`Component ${target.name} overides translations for locale "${locale}" that is not in your bundles`);
                    return null;
                }
                const bundle = inheritBundle(locales, parentBundle);
                bundle.addResource(resources, { allowOverrides: true });
                return bundle;
            })
                .filter((bundle) => bundle != null);
        });
        const allBundles = computed(() => overriddenBundles.value.concat(rootContext.bundles.value));
        context = new TranslationContext(rootContext.locale, allBundles);
    }
    target._fluent = context;
    return context;
}
function useFluent() {
    const instance = getCurrentInstance();
    if (instance == null) {
        const error = 'useFluent called outside of setup';
        warn(error);
        throw error;
    }
    const rootContext = inject(RootContextSymbol);
    if (rootContext == null) {
        const error = 'useFluent called without instaling plugin';
        warn(error);
        throw error;
    }
    return getContext(rootContext, instance);
}

// This part is from fluent-dom library
const LOCALIZABLE_ATTRIBUTES = {
    global: ['title', 'aria-label', 'aria-valuetext', 'aria-moz-hint'],
    a: ['download'],
    area: ['download', 'alt'],
    // value is special-cased in isAttrNameLocalizable
    input: ['alt', 'placeholder'],
    menuitem: ['label'],
    menu: ['label'],
    optgroup: ['label'],
    option: ['label'],
    track: ['label'],
    img: ['alt'],
    textarea: ['placeholder'],
    th: ['abbr'],
};
/**
 * Check if attribute is allowed for the given element.
 *
 * This method is used by the sanitizer when the translation markup contains
 * DOM attributes, or when the translation has traits which map to DOM
 * attributes.
 *
 * `explicitlyAllowed` can be passed as a list of attributes explicitly
 * allowed on this element.
 *
 * @param   {string}         name
 * @param   {Element}        element
 * @param   {Array}          explicitlyAllowed
 * @returns {boolean}
 * @private
 */
function isAttrNameLocalizable(name, element, explicitlyAllowed = null) {
    if (explicitlyAllowed && explicitlyAllowed.includes(name)) {
        return true;
    }
    if (element.namespaceURI === null) {
        return false;
    }
    const attrName = name.toLowerCase();
    const elemName = element.localName;
    // Is it a globally safe attribute?
    if (LOCALIZABLE_ATTRIBUTES.global.includes(attrName)) {
        return true;
    }
    // Are there no allowed attributes for this element?
    if (!LOCALIZABLE_ATTRIBUTES[elemName]) {
        return false;
    }
    // Is it allowed on this element?
    if (LOCALIZABLE_ATTRIBUTES[elemName].includes(attrName)) {
        return true;
    }
    // Special case for value on HTML inputs with type button, reset, submit
    if (element.namespaceURI === 'http://www.w3.org/1999/xhtml' &&
        elemName === 'input' &&
        attrName === 'value') {
        const type = element.type.toLowerCase();
        if (type === 'submit' || type === 'button' || type === 'reset') {
            return true;
        }
    }
    return false;
}
function translate(el, fluent, binding) {
    const key = binding.arg;
    if (key === undefined) {
        warn('v-t directive is missing arg with translation key');
        return;
    }
    const translation = fluent.formatWithAttrs(key, binding.value);
    if (translation.hasValue) {
        el.textContent = translation.value;
    }
    const allowedAttrs = Object.keys(binding.modifiers);
    for (const [attr, attrValue] of Object.entries(translation.attributes)) {
        if (isAttrNameLocalizable(attr, el, allowedAttrs)) {
            el.setAttribute(attr, attrValue);
        }
    }
}
function createVue3Directive(rootContext) {
    return {
        beforeMount(el, binding, vnode) {
            if (binding.instance == null) {
                return;
            }
            const context = getContext(rootContext, binding.instance);
            translate(el, context, binding);
        },
        updated(el, binding, vnode) {
            if (binding.instance == null) {
                return;
            }
            const context = getContext(rootContext, binding.instance);
            translate(el, context, binding);
        },
    };
}
function createVue2Directive(rootContext) {
    return {
        bind(el, binding, vnode) {
            if (vnode.context == null) {
                return;
            }
            const context = getContext(rootContext, vnode.context);
            translate(el, context, binding);
        },
        update(el, binding, vnode) {
            if (vnode.context == null) {
                return;
            }
            const context = getContext(rootContext, vnode.context);
            translate(el, context, binding);
        },
    };
}

const camelizeRE = /-(\w)/g;
function camelizeSub(_, c) {
    return c ? c.toUpperCase() : '';
}
function camelize(str) {
    return str.replace(camelizeRE, camelizeSub);
}

function getParentWithFluent(instance) {
    var _a, _b, _c;
    const parent = (_a = instance === null || instance === void 0 ? void 0 : instance.$parent) !== null && _a !== void 0 ? _a : (_b = instance === null || instance === void 0 ? void 0 : instance.parent) === null || _b === void 0 ? void 0 : _b.proxy;
    const target = (_c = parent === null || parent === void 0 ? void 0 : parent.$options) !== null && _c !== void 0 ? _c : parent === null || parent === void 0 ? void 0 : parent.type;
    if (target != null && target.fluent == null) {
        return getParentWithFluent(parent);
    }
    return parent;
}
var component = defineComponent({
    name: 'i18n',
    props: {
        path: { type: String, required: true },
        tag: { type: String, default: 'span' },
        args: { type: Object, default: () => ({}) },
    },
    setup(props, context) {
        const childSlots = context.slots;
        const rootContext = inject(RootContextSymbol);
        const instance = getCurrentInstance();
        const parent = getParentWithFluent(instance);
        const fluent = getContext(rootContext, parent);
        const fluentParams = computed(() => Object.assign({}, props.args, ...Object.keys(childSlots).map((key) => ({
            [key]: `\uFFFF\uFFFE${key}\uFFFF`,
        }))));
        const translation = computed(() => {
            return fluent.formatWithAttrs(props.path, fluentParams.value);
        });
        const camelizedAttrs = computed(() => Object.fromEntries(Object.entries(translation.value.attributes).map(([key, value]) => [camelize(key), value])));
        return () => h(props.tag, {
            ...context,
        }, translation.value.value
            .split('\uFFFF')
            .map((text) => {
            var _a;
            return text.startsWith('\uFFFE')
                ? (_a = childSlots[text.replace('\uFFFE', '')]) === null || _a === void 0 ? void 0 : _a.call(childSlots, camelizedAttrs.value)
                : text;
        }));
    },
});

/**
 * Creates FluentVue instance that can bu used on a Vue app.
 *
 * @param options - {@link FluentVueOptions}
 */
function createFluentVue(options) {
    const locale = ref(options.locale);
    const bundles = ref(options.bundles);
    const rootContext = new TranslationContext(locale, bundles);
    return {
        get locale() {
            return locale.value;
        },
        set locale(value) {
            locale.value = value;
        },
        get bundles() {
            return bundles.value;
        },
        set bundles(value) {
            bundles.value = value;
        },
        format: rootContext.format.bind(rootContext),
        formatAttrs: rootContext.formatAttrs.bind(rootContext),
        install(vue) {
            if (isVue3) {
                vue.provide(RootContextSymbol, rootContext);
                vue.config.globalProperties.$t = function (key, value) {
                    return getContext(rootContext, this).format(key, value);
                };
                vue.config.globalProperties.$ta = function (key, value) {
                    return getContext(rootContext, this).formatAttrs(key, value);
                };
                vue.directive('t', createVue3Directive(rootContext));
            }
            else {
                vue.mixin({
                    setup() {
                        provide(RootContextSymbol, rootContext);
                    },
                });
                vue.prototype.$t = function (key, value) {
                    return getContext(rootContext, this).format(key, value);
                };
                vue.prototype.$ta = function (key, value) {
                    return getContext(rootContext, this).formatAttrs(key, value);
                };
                vue.directive('t', createVue2Directive(rootContext));
            }
            vue.component('i18n', component);
        },
    };
}

export { createFluentVue, useFluent };
